// ── Region (client single point) — the tenant's time zone, currency, locales, calendar ───────────
// Mirror of server/src/services/region.ts. The values arrive on /api/me as `me.region` (Admin →
// Companies → Workspace → Region) and are read from the store here, so no component ever writes
// a zone, a currency or a locale into code again. Everything works before login and on no-login
// pages too: with no region the browser's own zone, 'en-US' numbers, ISO dates and no symbol apply.
// Non-React callers read the store with useStore.getState(); components that render the region
// itself use the useRegion() hook so they re-render when /api/me lands.
import { useMemo } from 'react';
import { useStore } from '../store';

export type RegionCalendar = 'gregorian' | 'buddhist-dual';
export type Region = {
  timezone: string;         // IANA zone
  currency: string;         // ISO 4217 code ('' = unknown)
  currencySymbol: string;   // '' = print the code instead
  localeNumber: string;     // BCP 47 for numbers / money
  localeDate: string;       // BCP 47 for dates
  calendar: RegionCalendar; // whether a local calendar year (+543) is printed beside the Gregorian one
  dateFormat: string;       // DD/MM/YYYY | MM/DD/YYYY | YYYY-MM-DD
  country: string;          // ISO 3166-1 alpha-2
  dialCode: string;         // e.g. +66
};

const browserZone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

// A page that has no login (public share / portal) may hand in the region its own bootstrap
// fetched; it is used only while the store holds no `me.region`.
let override: Partial<Region> | null = null;
export const setRegionOverride = (r: Partial<Region> | null): void => { override = r; };

const normalise = (r: Partial<Region> | null | undefined): Region => ({
  timezone: (r?.timezone || '').trim() || browserZone(),
  currency: (r?.currency || '').trim().toUpperCase(),
  currencySymbol: (r?.currencySymbol || '').trim(),
  localeNumber: (r?.localeNumber || '').trim() || 'en-US',
  localeDate: (r?.localeDate || '').trim() || 'en-US',
  calendar: r?.calendar === 'buddhist-dual' ? 'buddhist-dual' : 'gregorian',
  dateFormat: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(r?.dateFormat || '') ? String(r!.dateFormat) : 'YYYY-MM-DD',
  country: (r?.country || '').trim().toUpperCase(),
  dialCode: (r?.dialCode || '').trim(),
});

/** The region as the store holds it right now (safe fallbacks when nobody is logged in yet). */
export function region(): Region {
  let r: Partial<Region> | undefined;
  try { r = useStore.getState().me?.region; } catch { /* store not ready */ }
  return normalise(r || override || null);
}
/** Hook form of region() for components that render the region itself. */
export function useRegion(): Region {
  const raw = useStore(s => s.me?.region);
  return useMemo(() => normalise(raw || override || null), [raw]);
}

// ── Per-company regions (owner 2026-10-03: every company may have its own currency / formats) ─────
// A list can hold documents of several firms, so each row formats in ITS company's region. The map
// (GET /api/money/regions, loaded once by utils/companyRegions.ts; the client portal adds its merchant)
// holds each company's effective region. A company not in the map — or no company — reads region().
let companyRegions: Record<string, Partial<Region>> = {};
let companyRegionsVer = 0;
const companyRegionListeners = new Set<() => void>();
/** Merge regions into the map (by company id) and re-render subscribers. */
export function addCompanyRegions(m: Record<string, Partial<Region> | null | undefined> | null | undefined): void {
  let changed = false;
  for (const [id, r] of Object.entries(m || {})) { if (id && r) { companyRegions = { ...companyRegions, [id]: r }; changed = true; } }
  if (changed) { companyRegionsVer++; companyRegionListeners.forEach(f => f()); }
}
/** Forget every company region (a different login); subscribers re-render on the active region. */
export function clearCompanyRegions(): void {
  if (!Object.keys(companyRegions).length) return;
  companyRegions = {}; companyRegionsVer++; companyRegionListeners.forEach(f => f());
}
export const subscribeCompanyRegions = (f: () => void): (() => void) => { companyRegionListeners.add(f); return () => { companyRegionListeners.delete(f); }; };
export const companyRegionsVersion = (): number => companyRegionsVer;
/** The region one company lives in: its entry in the map, else the active region (region()). */
export function regionOf(companyId?: string | null): Region {
  const id = String(companyId || '').trim();
  const r = id ? companyRegions[id] : undefined;
  return r ? normalise(r) : region();
}

// ── Time ─────────────────────────────────────────────────────────────────────────────────────────
/** The tenant's IANA time zone (the browser's own zone until /api/me has arrived). */
export const tz = (): string => region().timezone;
/** A short human label for the zone — the IANA city with spaces ("Bangkok", "Kuala Lumpur"); the zone itself when it has no city. */
export const tzLabel = (): string => { const z = tz(); const city = z.includes('/') ? z.slice(z.lastIndexOf('/') + 1) : z; return city.replace(/_/g, ' '); };

const partsOf = (d: Date, zone: string) => {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, weekday: 'short',
  }).formatToParts(d);
  const v = (t: string) => p.find(x => x.type === t)?.value || '';
  return {
    y: Number(v('year')), m: Number(v('month')), d: Number(v('day')),
    hh: Number(v('hour')) % 24, mm: Number(v('minute')), ss: Number(v('second')) % 60,
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(v('weekday')),
  };
};
const pad2 = (n: number) => String(n).padStart(2, '0');

/** The parts of a moment in the tenant's zone, for arithmetic. */
export function zonedParts(d: Date = new Date()): { y: number; m: number; d: number; hh: number; mm: number; dow: number } {
  const { y, m, d: dd, hh, mm, dow } = partsOf(d, tz());
  return { y, m, d: dd, hh, mm, dow };
}
/** YYYY-MM-DD of a moment in the tenant's zone (default: now). */
export function todayISO(d: Date = new Date()): string {
  const p = zonedParts(d);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
}
/** YYYY-MM of a moment in the tenant's zone. */
export const monthISO = (d: Date = new Date()): string => todayISO(d).slice(0, 7);
/** HH:MM (24h) of a moment in the tenant's zone. */
export function timeHM(d: Date = new Date()): string {
  const p = zonedParts(d);
  return `${pad2(p.hh)}:${pad2(p.mm)}`;
}

// Wall-clock ⇄ real moment in the tenant's zone (DST-safe: the offset is read from Intl, twice).
const zoneOffsetMs = (t: number, zone: string): number => {
  const p = partsOf(new Date(t), zone);
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss) - Math.floor(t / 1000) * 1000;
};
/** A wall-clock reading in the tenant's zone → the real moment (ms). Day may overflow (d + n). */
export function zonedWallToMs(y: number, m: number, d: number, hh = 0, mm = 0): number {
  const zone = tz();
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const first = guess - zoneOffsetMs(guess, zone);
  return guess - zoneOffsetMs(first, zone);
}
/** ms → 'YYYY-MM-DDTHH:mm' in the tenant's zone, the value an <input type=datetime-local> takes. */
export function msToZonedInput(ms: number): string {
  const p = zonedParts(new Date(ms));
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}T${pad2(p.hh)}:${pad2(p.mm)}`;
}
/** 'YYYY-MM-DDTHH:mm' read as the tenant's wall clock → ms (NaN when incomplete). */
export function zonedInputToMs(v: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v || '');
  return m ? zonedWallToMs(+m[1], +m[2], +m[3], +m[4], +m[5]) : NaN;
}

// ── Dates on paper ────────────────────────────────────────────────────────────────────────────────
/** The local calendar year printed beside the Gregorian one (Buddhist = +543), or null when the region prints one year only. */
export function localYear(gregorianYear: number, companyId?: string | null): number | null {
  return regionOf(companyId).calendar === 'buddhist-dual' ? gregorianYear + 543 : null;
}
/** 'YYYY-MM-DD' → the region's date format (DD/MM/YYYY …). Unparseable input is returned as-is. */
export function fmtDate(iso: string, opts: { withLocalYear?: boolean; companyId?: string | null } = {}): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const [y, mo, d] = [m[1], m[2], m[3]];
  const f = regionOf(opts.companyId).dateFormat;
  const out = f === 'DD/MM/YYYY' ? `${d}/${mo}/${y}` : f === 'MM/DD/YYYY' ? `${mo}/${d}/${y}` : `${y}-${mo}-${d}`;
  const ly = opts.withLocalYear ? localYear(Number(y), opts.companyId) : null;
  return ly ? `${out} (${ly})` : out;
}
/** A moment (unix seconds, ms, or Date) → region date + HH:MM in the tenant's zone. '' for 0/null. */
export function fmtDateTime(unixOrDate: number | Date | null | undefined): string {
  if (!unixOrDate) return '';
  const d = unixOrDate instanceof Date ? unixOrDate : new Date(unixOrDate < 1e12 ? unixOrDate * 1000 : unixOrDate);
  if (isNaN(d.getTime())) return '';
  return `${fmtDate(todayISO(d))} ${timeHM(d)}`;
}

/** A long date in the region's date locale, e.g. "30 September 2026". */
export function fmtDateLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  try { return new Intl.DateTimeFormat(localeDate(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]))); }
  catch { return `${m[3]}/${m[2]}/${m[1]}`; }
}
/** The region's date locale (BCP 47), e.g. 'en-GB' — for Intl calls that need one. */
export const localeDate = (): string => region().localeDate || 'en-GB';
/** The region's number locale (BCP 47), e.g. 'en-US' — a company's own when `companyId` names one in the map. */
export const localeNumber = (companyId?: string | null): string => regionOf(companyId).localeNumber || 'en-US';
/** Integer / plain number in the region's number locale (no forced decimals unless `digits` is given). */
export function fmtNumber(n: any, digits?: number, companyId?: string | null): string {
  const v = Number(n); const x = Number.isFinite(v) ? v : 0;
  try { return x.toLocaleString(localeNumber(companyId), digits == null ? undefined : { minimumFractionDigits: digits, maximumFractionDigits: digits }); }
  catch { return digits == null ? String(x) : x.toFixed(digits); }
}

// ── Money ─────────────────────────────────────────────────────────────────────────────────────────
// Every helper takes an optional company: a document's amounts print in ITS company's region.
export const currencyCode = (companyId?: string | null): string => regionOf(companyId).currency;
export const currencySymbol = (companyId?: string | null): string => { const r = regionOf(companyId); return r.currencySymbol || r.currency; };
/** Thousands-separated amount in the region's number locale — no symbol. */
export function money(n: any, digits = 2, companyId?: string | null): string {
  const v = Number(n); const x = Number.isFinite(v) ? v : 0;
  try { return x.toLocaleString(localeNumber(companyId), { minimumFractionDigits: digits, maximumFractionDigits: digits }); }
  catch { return x.toFixed(digits); }
}
/** Amount with the region's currency: symbol (or ISO code) + ' ' + money(n); bare amount when neither is set. */
export function moneyWithCurrency(n: any, digits = 2, companyId?: string | null): string {
  const sym = currencySymbol(companyId);
  return sym ? `${sym} ${money(n, digits, companyId)}` : money(n, digits, companyId);
}
/** Symbol glued to the amount, no space — "฿82,390.00" — for the compact figures in lists and pills. */
export const moneyTight = (n: any, digits = 2, companyId?: string | null): string => `${currencySymbol(companyId)}${money(n, digits, companyId)}`;
